import { autoLevels, detectReceipt, solveHomography, toGray, toPaperness, type Detection, type Point } from './receipt-detect';

/**
 * Turns a camera frame or a picked photo into the image the AI reads best:
 * the receipt alone, straightened, with restored contrast.
 * If no complete receipt is found, the whole photo is used (just resized).
 *
 * Canvas memory matters on iPhones: WebKit caps the total per page, and a
 * failed allocation returns no context. So detection reuses one small
 * canvas, and every other canvas is released (0×0) as soon as it's done.
 */

/** Longest side sent to the AI: small receipt print stays legible. */
const MAX_OUTPUT = 2000;
/**
 * Small crops are enlarged to at least this: vision models see small digits
 * as a few blurry pixels (a 5 reads as a 3), larger text reads reliably.
 */
const MIN_OUTPUT = 1280;

/** Output size for a crop: at most MAX_OUTPUT, at least MIN_OUTPUT (up to 3× enlargement). */
export function outputScale(longSide: number): number {
  const target = Math.min(MAX_OUTPUT, Math.max(MIN_OUTPUT, longSide));
  return Math.min(3, target / longSide);
}
/** Working resolution for the warp. */
const MAX_SOURCE = 2400;
const DETECT_SIDE = 320;

export interface Frame {
  source: CanvasImageSource;
  width: number;
  height: number;
}

function canvasOf(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Canvas unavailable');
  return [canvas, context];
}

/** Frees a canvas's pixel memory right away (WebKit otherwise keeps it until GC). */
export function release(...canvases: HTMLCanvasElement[]): void {
  for (const canvas of canvases) {
    canvas.width = 0;
    canvas.height = 0;
  }
}

let detectCanvas: [HTMLCanvasElement, CanvasRenderingContext2D] | null = null;

export interface FrameAnalysis {
  detection: Detection | null;
  /** Grayscale of the small frame (for sharpness). */
  gray: Uint8ClampedArray;
  w: number;
  h: number;
}

/** Finds the receipt (corners in the frame's own pixels), reusing one small canvas. */
export function analyzeFrame(frame: Frame): FrameAnalysis {
  const scale = Math.min(1, DETECT_SIDE / Math.max(frame.width, frame.height));
  const w = Math.max(1, Math.round(frame.width * scale));
  const h = Math.max(1, Math.round(frame.height * scale));
  detectCanvas ??= canvasOf(w, h);
  const [canvas, context] = detectCanvas;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  context.drawImage(frame.source, 0, 0, w, h);
  const { data } = context.getImageData(0, 0, w, h);
  const found = detectReceipt(toPaperness(data), w, h);
  return {
    detection: found && {
      ...found,
      corners: found.corners.map((c) => ({ x: c.x / scale, y: c.y / scale })) as Detection['corners'],
    },
    gray: toGray(data),
    w,
    h,
  };
}

export const detectInFrame = (frame: Frame): Detection | null => analyzeFrame(frame).detection;

/** Pushes corners slightly outwards so the warp never clips the receipt's edge. */
function expand(corners: Point[], by = 0.03): Point[] {
  const cx = corners.reduce((s, c) => s + c.x, 0) / 4;
  const cy = corners.reduce((s, c) => s + c.y, 0) / 4;
  return corners.map((c) => ({ x: c.x + (c.x - cx) * by, y: c.y + (c.y - cy) * by }));
}

const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Encoding failed'))), 'image/jpeg', 0.88),
  );
}

/** The whole frame, resized and contrast-restored (no crop). */
async function wholeFrame(source: HTMLCanvasElement): Promise<Blob> {
  const scale = outputScale(Math.max(source.width, source.height));
  const [out, outContext] = canvasOf(source.width * scale, source.height * scale);
  try {
    outContext.drawImage(source, 0, 0, out.width, out.height);
    const pixels = outContext.getImageData(0, 0, out.width, out.height);
    autoLevels(pixels.data);
    outContext.putImageData(pixels, 0, 0);
    return await toJpeg(out);
  } finally {
    release(out);
  }
}

export async function prepareReceipt(frame: Frame, detection?: Detection | null): Promise<Blob> {
  const detected = detection === undefined ? detectInFrame(frame) : detection;
  // Only crop to a receipt that is fully in view: cropping a partial one
  // could cut off exactly the part (the total) we need.
  const found = detected?.complete ? detected : null;

  // A bounded working copy of the source.
  const sourceScale = Math.min(1, MAX_SOURCE / Math.max(frame.width, frame.height));
  const [source, sourceContext] = canvasOf(frame.width * sourceScale, frame.height * sourceScale);
  try {
    sourceContext.fillStyle = '#fff';
    sourceContext.fillRect(0, 0, source.width, source.height);
    sourceContext.drawImage(frame.source, 0, 0, source.width, source.height);
    if (!found) return await wholeFrame(source);

    const corners = expand(found.corners.map((c) => ({ x: c.x * sourceScale, y: c.y * sourceScale })));
    const [tl, tr, br, bl] = corners as [Point, Point, Point, Point];
    let width = (dist(tl, tr) + dist(bl, br)) / 2;
    let height = (dist(tl, bl) + dist(tr, br)) / 2;
    const fit = outputScale(Math.max(width, height));
    width = Math.max(1, Math.round(width * fit));
    height = Math.max(1, Math.round(height * fit));

    // Inverse mapping: for each output pixel, where is it in the photo?
    const h = solveHomography(
      [
        { x: 0, y: 0 },
        { x: width - 1, y: 0 },
        { x: width - 1, y: height - 1 },
        { x: 0, y: height - 1 },
      ],
      corners,
    );
    const src = sourceContext.getImageData(0, 0, source.width, source.height);
    const [out, outContext] = canvasOf(width, height);
    try {
      const dst = outContext.createImageData(width, height);
      const sw = source.width;
      const sh = source.height;
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const d = h[6]! * x + h[7]! * y + 1;
          const sx = (h[0]! * x + h[1]! * y + h[2]!) / d;
          const sy = (h[3]! * x + h[4]! * y + h[5]!) / d;
          // Bilinear sample.
          const x0 = Math.min(sw - 2, Math.max(0, Math.floor(sx)));
          const y0 = Math.min(sh - 2, Math.max(0, Math.floor(sy)));
          const fx = Math.min(1, Math.max(0, sx - x0));
          const fy = Math.min(1, Math.max(0, sy - y0));
          const i00 = (y0 * sw + x0) * 4;
          const i10 = i00 + 4;
          const i01 = i00 + sw * 4;
          const i11 = i01 + 4;
          const o = (y * width + x) * 4;
          for (let c = 0; c < 3; c++) {
            const top = src.data[i00 + c]! * (1 - fx) + src.data[i10 + c]! * fx;
            const bottom = src.data[i01 + c]! * (1 - fx) + src.data[i11 + c]! * fx;
            dst.data[o + c] = top * (1 - fy) + bottom * fy;
          }
          dst.data[o + 3] = 255;
        }
      }
      autoLevels(dst.data);
      outContext.putImageData(dst, 0, 0);
      return await toJpeg(out);
    } finally {
      release(out);
    }
  } finally {
    release(source);
  }
}

/** A picked photo (gallery or the phone's own camera app) → prepared receipt. */
export async function prepareReceiptFile(file: File): Promise<Blob> {
  // createImageBitmap applies the photo's EXIF orientation.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    return await prepareReceipt({ source: bitmap, width: bitmap.width, height: bitmap.height });
  } finally {
    bitmap.close();
  }
}
